/**
 * SmartNoteBoardPanel
 * ───────────────────
 * Read-only side panel that shows the board OCR content + voice↔board
 * comparison that came out of the Auto-Sync Multimodal flow.
 *
 * Reads from `draft.content` (which is what /save-to-draft writes):
 *   - voice_only_insights[]
 *   - board_only_artifacts[]
 *   - high_confidence_concepts[]
 *   - teacher_emphasis[]
 *   - missing_from_board[], missing_from_audio[]
 *   - cross_validation_score
 *   - suggested_quiz_questions[]
 *
 * Designed to be collapsed by default to keep the existing editor focused.
 */
import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Mic2, ClipboardCheck, AlertTriangle, Trophy, Quote, Brain, Sparkles } from 'lucide-react';

function Section({ icon: Icon, title, accent = '#0EA5E9', count, defaultOpen = false, children, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0 && !defaultOpen) return null;
  return (
    <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid={testid}>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left bg-slate-50 hover:bg-slate-100 transition-colors"
        type="button">
        {open ? <ChevronDown className="w-3.5 h-3.5 text-slate-500" /> : <ChevronRight className="w-3.5 h-3.5 text-slate-500" />}
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="text-xs font-semibold flex-1" style={{ color: accent }}>{title}</span>
        {typeof count === 'number' && (
          <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: `${accent}15`, color: accent }}>{count}</span>
        )}
      </button>
      {open && <div className="p-3 bg-white">{children}</div>}
    </div>
  );
}

export default function SmartNoteBoardPanel({ draft }) {
  const c = (draft && draft.content) || {};
  const hasSmartNote =
    Array.isArray(c.voice_only_insights) || Array.isArray(c.board_only_artifacts) ||
    Array.isArray(c.high_confidence_concepts) || typeof c.cross_validation_score === 'number';
  if (!hasSmartNote) return null;

  const score = Math.round((c.cross_validation_score || 0) * 100);
  const color = score >= 70 ? '#10B981' : score >= 40 ? '#F59E0B' : '#EF4444';

  return (
    <div
      className="rounded-2xl border border-green-200 bg-green-50/30 p-4 space-y-3"
      data-testid="smart-note-board-panel">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white" style={{ background: '#1A7F37' }}>
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-semibold text-green-700">Voice ↔ Board Smart Note</div>
          <div className="text-sm font-semibold text-slate-900">Generated from Screening (voice + board fusion)</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-xs text-slate-500 font-semibold">Match</div>
          <div className="text-2xl font-semibold tabular-nums" style={{ color }}>{score}%</div>
        </div>
      </div>

      <Section icon={Trophy} title="High-confidence concepts" accent="#10B981"
        count={(c.high_confidence_concepts || []).length} defaultOpen
        testid="smart-note-high-confidence">
        <ul className="space-y-2">
          {(c.high_confidence_concepts || []).map((x, i) => (
            <li key={i} className="text-xs text-slate-800">
              <div className="font-semibold">{x.concept}</div>
              {x.audio_evidence && <div className="text-xs text-slate-500">🎙️ {x.audio_evidence.slice(0, 160)}</div>}
              {x.board_evidence && <div className="text-xs text-slate-500">📝 {x.board_evidence.slice(0, 160)}</div>}
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={ClipboardCheck} title="Board-only (written, not said)" accent="#F59E0B"
        count={(c.board_only_artifacts || []).length} testid="smart-note-board-only">
        <ul className="grid md:grid-cols-2 gap-1.5">
          {(c.board_only_artifacts || []).map((b, i) => (
            <li key={i} className="text-xs text-slate-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5">
              {typeof b === 'string' ? b : (b.artifact || JSON.stringify(b))}
            </li>
          ))}
        </ul>
      </Section>

      <Section icon={Mic2} title="Voice-only (said, not written)" accent="#0EA5E9"
        count={(c.voice_only_insights || []).length} testid="smart-note-voice-only">
        <ul className="grid md:grid-cols-2 gap-1.5">
          {(c.voice_only_insights || []).map((v, i) => (
            <li key={i} className="text-xs text-slate-700 bg-blue-50 border border-blue-200 rounded-md px-2.5 py-1.5">{v}</li>
          ))}
        </ul>
      </Section>

      <Section icon={Quote} title="Teacher emphasis" accent="#8B5CF6"
        count={(c.teacher_emphasis || []).length} testid="smart-note-emphasis">
        <div className="flex flex-wrap gap-1.5">
          {(c.teacher_emphasis || []).map((t, i) => (
            <span key={i} className="text-xs font-semibold px-2 py-1 rounded-md bg-slate-50 text-slate-900 border border-slate-200">
              {t.term} <span className="opacity-60 font-normal">·{t.repetition_count}×</span>
            </span>
          ))}
        </div>
      </Section>

      <Section icon={AlertTriangle} title="Coverage gaps" accent="#EF4444"
        count={(c.missing_from_board || []).length + (c.missing_from_audio || []).length}
        testid="smart-note-gaps">
        {(c.missing_from_board || []).length > 0 && (
          <>
            <div className="text-xs font-semibold text-red-700 mb-1">Spoken but missing from board</div>
            <ul className="text-xs text-slate-700 space-y-1 mb-2">
              {c.missing_from_board.map((m, i) => (
                <li key={i}>• <span className="font-semibold">{m.concept}</span> — {m.recommendation || '—'}</li>
              ))}
            </ul>
          </>
        )}
        {(c.missing_from_audio || []).length > 0 && (
          <>
            <div className="text-xs font-semibold text-red-700 mb-1">On board but never spoken</div>
            <ul className="text-xs text-slate-700 space-y-1">
              {c.missing_from_audio.map((m, i) => (
                <li key={i}>• <span className="font-semibold">{m.artifact}</span> · severity {m.severity || 'low'} — {m.recommendation || '—'}</li>
              ))}
            </ul>
          </>
        )}
      </Section>

      <Section icon={Brain} title="Suggested quiz" accent="#0EA5E9"
        count={(c.suggested_quiz_questions || []).length} testid="smart-note-quiz">
        <ol className="space-y-2 list-decimal ml-5">
          {(c.suggested_quiz_questions || []).map((q, i) => (
            <li key={i} className="text-xs text-slate-800">
              <div className="font-semibold">{q.question}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                <span className="inline-block px-1.5 py-0.5 rounded-full bg-slate-100">{q.type}</span>{' '}
                <span className="inline-block px-1.5 py-0.5 rounded-full bg-slate-100">{q.difficulty}</span>
                {q.answer_hint && <> · hint: {q.answer_hint}</>}
              </div>
            </li>
          ))}
        </ol>
      </Section>
    </div>
  );
}
